import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import arrowLeft from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import { couponService } from "../services";
import { CouponData } from "../utils/types";
import AddCoupon from "./AddCoupon";

type CouponsProps = {
  setIsCouponsOpen: (open: boolean) => void;
};

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const getStatus = (coupon: CouponData) => {
  const isExpired = new Date(coupon.endDate) < new Date();
  const isUpcoming = new Date(coupon.startDate) > new Date();
  if (!coupon.isActive) return { text: "Inactive", color: "text-red-500 bg-red-50" };
  if (isExpired) return { text: "Expired", color: "text-orange-500 bg-orange-50" };
  if (isUpcoming) return { text: "Upcoming", color: "text-blue-500 bg-blue-50" };
  return { text: "Active", color: "text-green-600 bg-green-50" };
};

const Coupons = ({ setIsCouponsOpen }: CouponsProps) => {
  const [coupons, setCoupons] = useState<CouponData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const fetchCoupons = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await couponService.getCoupons(1, 50);
      if (response.status && Array.isArray(response.data)) {
        setCoupons(response.data);
      } else {
        setCoupons([]);
      }
    } catch (error: any) {
      console.error("Failed to fetch coupons:", error);
      toast.error(error?.message || "Failed to load coupons");
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Deactivate this coupon? Customers will no longer be able to use it.")) {
      return;
    }
    try {
      const response: any = await couponService.deactivateCoupon(id);
      if (response.status) {
        setCoupons((prev) =>
          prev.map((coupon) =>
            coupon._id === id ? { ...coupon, isActive: false } : coupon
          )
        );
        toast.success("Coupon deactivated");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to deactivate coupon");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300"
      onClick={() => setIsCouponsOpen(false)}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-900 shadow-2xl rounded-l-[20px] overflow-y-auto flex flex-col"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 z-10 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={arrowLeft}
                alt="back"
                className="w-[36px] h-[36px] rounded-full border border-[rgba(112,107,102,0.25)] cursor-pointer p-2"
                onClick={() => setIsCouponsOpen(false)}
              />
              <h2 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[20px] font-semibold">
                Discounts & Coupons
              </h2>
            </div>
            <img
              src={cancel}
              alt="close"
              className="w-[36px] h-[36px] p-2 rounded-full border cursor-pointer"
              onClick={() => setIsCouponsOpen(false)}
            />
          </div>
        </div>

        <div className="flex-1 px-6 py-4 space-y-3">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8A00]"></div>
            </div>
          ) : coupons.length === 0 ? (
            <p className="text-center text-gray-500 py-16">
              No coupons yet. Create one to offer discounts to customers.
            </p>
          ) : (
            coupons.map((coupon) => {
              const status = getStatus(coupon);
              const discountText =
                coupon.discountType === "percentage"
                  ? `${coupon.discountValue}% OFF`
                  : `₹${coupon.discountValue} OFF`;
              return (
                <div
                  key={coupon._id}
                  className="border dark:border-gray-700 rounded-2xl p-4 bg-white dark:bg-gray-800"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[18px] font-semibold tracking-wide">
                        {coupon.couponCode}
                      </p>
                      <p className="text-[#6A6A6A] dark:text-gray-400 text-sm mt-1">
                        {coupon.description}
                      </p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${status.color}`}>
                      {status.text}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                    <p className="text-[#FF8A00] font-semibold">{discountText}</p>
                    <p className="text-[#6A6A6A] dark:text-gray-400">
                      Min ₹{coupon.minOrderValue}
                    </p>
                    {coupon.maxDiscountCap ? (
                      <p className="text-[#6A6A6A] dark:text-gray-400">
                        Max cap ₹{coupon.maxDiscountCap}
                      </p>
                    ) : null}
                    <p className="text-[#6A6A6A] dark:text-gray-400">
                      Used {coupon.usageCount || 0}/{coupon.usageLimitGlobal}
                    </p>
                  </div>
                  <p className="text-xs text-[#6A6A6A] dark:text-gray-400 mt-2">
                    {formatDate(coupon.startDate)} – {formatDate(coupon.endDate)}
                  </p>
                  {coupon.isActive && new Date(coupon.endDate) >= new Date() && (
                    <button
                      onClick={() => handleDeactivate(coupon._id)}
                      className="mt-3 text-red-500 text-sm font-medium"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="p-6 pt-0">
          <button
            onClick={() => setIsAddOpen(true)}
            className="w-full py-4 text-white font-Urbanist text-[16px] font-semibold"
            style={{ borderRadius: 150, background: "#F30" }}
          >
            Add Coupon
          </button>
        </div>
      </div>

      {isAddOpen && (
        <AddCoupon
          setIsAddCouponOpen={setIsAddOpen}
          onCreated={fetchCoupons}
        />
      )}
    </div>
  );
};

export default Coupons;
